import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowCounterClockwiseIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import type { ChangeSetCommentThread } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { CommentBody } from '@/components/common/comments/CommentBody';
import { NoteComposer } from '@/components/common/comments/NoteComposer';
import { NoteRow } from '@/components/common/comments/NoteRow';
import type { MentionCandidate } from '@/components/common/comments/mention-input';
import { cn } from '@/lib/utils';
import { threadUserNames } from './cellComments';
import { OrphanedTag, ResolvedTag } from './CommentThreadTags';

export interface CommentThreadProps {
  thread: ChangeSetCommentThread;
  /**
   * `full` (default): the interactive thread for the grid popover and the
   * comments panel. `compact`: a dense read-only rendering for a list cell
   * (Review mode's Comment column); every callback is ignored.
   */
  variant?: 'full' | 'compact';
  /** Lets the viewer's own messages read as "You" and offer edit / delete. */
  currentUserId?: string;
  /** Posts a reply. Omit to hide the reply box. Throwing surfaces a toast. */
  onReply?: (body: string) => Promise<void>;
  isReplying?: boolean;
  /** People the reply box offers after `@`. Omit to turn mentions off. */
  mentionableUsers?: readonly MentionCandidate[];
  /** Author only: rows offer edit / delete on the viewer's own messages. */
  onEditMessage?: (messageId: string, body: string) => Promise<void>;
  onDeleteMessage?: (messageId: string) => Promise<void>;
  /** Omit both to hide the resolve / reopen button. */
  onResolve?: () => Promise<void>;
  onReopen?: () => Promise<void>;
  className?: string;
}

/**
 * One cell comment thread of an Item Master review (GEN-3261): its messages,
 * oldest first, with author, relative time and an "edited" tag; a reply box;
 * edit / delete on the viewer's own messages; and resolve / reopen. A resolved
 * thread takes no replies until it is reopened.
 *
 * Presentational: the host passes the thread and the callbacks. For a thread
 * wired to the review's mutations, use `ReviewCommentThread`.
 */
export function CommentThread(props: CommentThreadProps) {
  if (props.variant === 'compact') {
    return (
      <CompactCommentThread thread={props.thread} className={props.className} />
    );
  }
  return <FullCommentThread {...props} />;
}

function FullCommentThread({
  thread,
  currentUserId,
  onReply,
  isReplying = false,
  mentionableUsers,
  onEditMessage,
  onDeleteMessage,
  onResolve,
  onReopen,
  className,
}: CommentThreadProps) {
  const gt = useGT();
  const resolved = thread.resolvedAt !== null;
  const userNameById = threadUserNames(thread);

  async function handleReply(body: string): Promise<boolean> {
    if (!onReply) return false;
    try {
      await onReply(body);
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to reply', {
              context: 'toast after replying to a cell comment failed',
            }),
      );
      return false;
    }
  }

  return (
    <div
      className={cn('flex min-w-0 flex-col gap-1.5', className)}
      data-testid="comment-thread"
    >
      <ThreadHeader thread={thread} onResolve={onResolve} onReopen={onReopen} />
      <ul className="flex flex-col gap-1.5">
        {thread.messages.map((message) => (
          <NoteRow
            key={message.id}
            comment={message}
            currentUserId={currentUserId}
            userNameById={userNameById}
            onUpdate={onEditMessage}
            onDelete={onDeleteMessage}
          />
        ))}
      </ul>
      {onReply && !resolved && (
        <NoteComposer
          onSubmit={handleReply}
          isSubmitting={isReplying}
          mentionableUsers={mentionableUsers}
          // The thread scrolls in its popover or panel, which would cut off a
          // list floating above a reply box near its top.
          mentionListPlacement="inline"
          placeholder={gt('Reply…', {
            context: 'placeholder for a reply to a cell comment thread',
          })}
        />
      )}
    </div>
  );
}

function ThreadHeader({
  thread,
  onResolve,
  onReopen,
}: Pick<CommentThreadProps, 'thread' | 'onResolve' | 'onReopen'>) {
  const resolved = thread.resolvedAt !== null;
  const toggle = resolved ? onReopen : onResolve;
  return (
    <div className="flex min-h-6 items-center justify-between gap-2 text-[10px] text-muted-foreground">
      <span className="flex min-w-0 items-center gap-1.5">
        {resolved && <ResolvedTag resolvedByName={thread.resolvedByName} />}
        {thread.orphaned && <OrphanedTag />}
      </span>
      {toggle && <ResolveToggle resolved={resolved} onToggle={toggle} />}
    </div>
  );
}

function ResolveToggle({
  resolved,
  onToggle,
}: {
  resolved: boolean;
  onToggle: () => Promise<void>;
}) {
  const gt = useGT();
  const [busy, setBusy] = useState(false);

  async function handleClick(): Promise<void> {
    setBusy(true);
    try {
      await onToggle();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to update the thread', {
              context: 'toast after resolving or reopening a comment failed',
            }),
      );
    }
    setBusy(false);
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="h-6 px-1.5 text-[11px]"
      disabled={busy}
      onClick={() => void handleClick()}
    >
      {resolved ? (
        <>
          <ArrowCounterClockwiseIcon className="size-3.5" />
          <T context="button that reopens a resolved cell comment thread">
            Reopen
          </T>
        </>
      ) : (
        <>
          <CheckCircleIcon className="size-3.5" />
          <T context="button that marks a cell comment thread as resolved">
            Resolve
          </T>
        </>
      )}
    </Button>
  );
}

/**
 * The read-only list-cell rendering: one clamped line per message,
 * `Author: text`, with the resolved and orphaned tags.
 */
function CompactCommentThread({
  thread,
  className,
}: {
  thread: ChangeSetCommentThread;
  className?: string;
}) {
  const gt = useGT();
  const unknownAuthor = gt('User', {
    context: 'cell comment author when the user name is unknown',
  });
  const userNameById = threadUserNames(thread);
  return (
    <div
      className={cn('flex min-w-0 flex-col gap-0.5 text-xs', className)}
      data-testid="comment-thread-compact"
    >
      {(thread.resolvedAt !== null || thread.orphaned) && (
        <span className="flex items-center gap-1">
          {thread.resolvedAt !== null && (
            <ResolvedTag resolvedByName={thread.resolvedByName} />
          )}
          {thread.orphaned && <OrphanedTag />}
        </span>
      )}
      {thread.messages.map((message) => (
        <p
          key={message.id}
          className={cn(
            'line-clamp-2 leading-snug text-stone-800',
            thread.resolvedAt !== null && 'text-muted-foreground',
          )}
        >
          <span className="font-medium text-stone-700">
            {message.authorName ?? unknownAuthor}
          </span>
          {': '}
          <CommentBody body={message.body} userNameById={userNameById} />
        </p>
      ))}
    </div>
  );
}
